<script setup lang="ts">
/**
 * The three taxonomy figures.
 *
 * Three rows and no way to add a fourth, because the regulation prescribes
 * exactly three. The shares are not fields: they are worked out from the
 * amounts and shown on the page.
 */
import { useI18n } from 'vue-i18n'

import { computed } from 'vue'

import type { TaxonomySection } from '@/domain/section'
import { sharesOf, TAXONOMY_KPIS, type TaxonomyKpi, type TaxonomyKpiId } from '@/domain/taxonomy'
import InspectorField from '@/sections/InspectorField.vue'

const { t } = useI18n()

const props = defineProps<{ section: TaxonomySection }>()
const emit = defineEmits<{ change: [section: TaxonomySection] }>()

const shares = computed(() => sharesOf(props.section.kpis))

function set(changes: Partial<TaxonomySection>): void {
  emit('change', { ...props.section, ...changes })
}

function setKpi(id: TaxonomyKpiId, changes: Partial<TaxonomyKpi>): void {
  set({ kpis: props.section.kpis.map((kpi) => (kpi.id === id ? { ...kpi, ...changes } : kpi)) })
}

function kpiOf(id: TaxonomyKpiId): TaxonomyKpi {
  return (
    props.section.kpis.find((kpi) => kpi.id === id) ?? { id, total: '', eligible: '', aligned: '' }
  )
}

function impossible(id: TaxonomyKpiId): boolean {
  return shares.value.find((share) => share.id === id)?.impossible === true
}
</script>

<template>
  <div class="fields">
    <div class="pair">
      <InspectorField :label="t('inspector.taxonomy.unit')">
        <input
          class="b-input"
          :value="section.unit"
          :placeholder="t('inspector.taxonomy.unitPlaceholder')"
          @input="set({ unit: ($event.target as HTMLInputElement).value })"
        />
      </InspectorField>

      <InspectorField :label="t('inspector.taxonomy.period')">
        <input
          class="b-input"
          :value="section.period"
          placeholder="2026"
          @input="set({ period: ($event.target as HTMLInputElement).value })"
        />
      </InspectorField>
    </div>

    <div v-for="id in TAXONOMY_KPIS" :key="id" class="kpi" :class="{ 'kpi--bad': impossible(id) }">
      <p class="kpi__name">{{ t(`inspector.taxonomy.kpis.${id}`) }}</p>

      <div class="kpi__row">
        <label class="kpi__field">
          <span>{{ t('inspector.taxonomy.total') }}</span>
          <input
            class="b-input"
            :value="kpiOf(id).total"
            @input="setKpi(id, { total: ($event.target as HTMLInputElement).value })"
          />
        </label>

        <label class="kpi__field">
          <span>{{ t('inspector.taxonomy.eligible') }}</span>
          <input
            class="b-input"
            :value="kpiOf(id).eligible"
            @input="setKpi(id, { eligible: ($event.target as HTMLInputElement).value })"
          />
        </label>

        <label class="kpi__field">
          <span>{{ t('inspector.taxonomy.aligned') }}</span>
          <input
            class="b-input"
            :value="kpiOf(id).aligned"
            @input="setKpi(id, { aligned: ($event.target as HTMLInputElement).value })"
          />
        </label>
      </div>

      <p v-if="impossible(id)" class="kpi__bad">{{ t('inspector.taxonomy.impossible') }}</p>
    </div>

    <InspectorField :label="t('inspector.taxonomy.caption')">
      <input
        class="b-input"
        :value="section.caption"
        @input="set({ caption: ($event.target as HTMLInputElement).value })"
      />
    </InspectorField>
  </div>
</template>

<style scoped>
.fields {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

.pair {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--sp-4);
}

.kpi {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  padding: var(--sp-4);
  border-radius: var(--radius);
  background: var(--panel-raised);
  box-shadow: inset 0 0 0 1px var(--hairline);
}

/* Raised where the figure is entered, not only where it is printed: this is
   something somebody has to go and fix. */
.kpi--bad {
  box-shadow: inset 0 0 0 1px var(--danger);
}

.kpi__name {
  margin: 0;
  color: var(--ink);
  font-size: var(--text-sm);
  font-weight: 600;
}

.kpi__row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--sp-2);
}

.kpi__field {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.kpi__field span {
  color: var(--ink-faint);
  font-size: var(--text-xs);
}

.kpi__field .b-input {
  min-width: 0;
  font-variant-numeric: tabular-nums;
  text-align: right;
}

.kpi__bad {
  margin: 0;
  color: var(--danger);
  font-size: var(--text-xs);
  line-height: 1.45;
}
</style>
